import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useHistory, useParams, } from 'react-router-dom';
import { GridColumn, Grid } from '@progress/kendo-react-grid';
import { StatusCodes } from 'http-status-codes';
import { Col, Form } from 'react-bootstrap';
import { OrderProductGetDTO } from 'svila-common';
import * as yup from 'yup';
import PuffLoader from 'react-spinners/PuffLoader';
import { API_URL, FILE_UPLOAD_TYPES, UPLOAD_MAX_SIZE_IMAGE } from '../../../../utils/constants';
import WhitePad from '../../../../components/common/WhitePad/WhitePad';
import { withState } from '../../../../components/common/grid/WrappedGrid';
import OrderProductsApi from '../../../../services/api/OrderProductsApi';
import SecondaryNavigation from '../../../../components/common/navigation/SecondaryNavigation';
import SecondaryButton from '../../../../components/common/forms/Button/SecondaryButton';
import BorderlessButton from '../../../../components/common/forms/Button/BorderlessButton';
import TitleComponent from '../../../../components/common/forms/Title/TitleComponent';
import { Formik } from 'formik';
import FileInputComponent from '~/src/components/common/forms/FileInput/FileInputComponent';
import FormRow from '~/src/components/common/forms/Row/FormRow';
import FilesApi from '~/src/services/api/FilesApi';
import ImportResults from '~/src/scenes/imports/components/ResultDtoDisplay';
import ProductService from '~/src/services/logic/ProductService';
import GtinComponent from '~/src/components/common/GtinComponent';
import styled from 'styled-components';
import TextInputComponent from '~/src/components/common/forms/TextInput/TextInputComponent';
import xIcon from '../../../../static/img/xIcon.svg';
import upload from '../../../../static/img/upload.svg';
import plus from '../../../../static/img/plus.svg';
import TableWrapper from '~/src/components/common/forms/TableWrapper/TableWrapper';
import importIcon from '../../../../static/img/importIcon.svg';
import DisplayQuantities from '~/src/components/common/DisplayQuantities';
import ModalWrapper from '~/src/components/common/Modal';
import xCircle from '../../../../static/img/xCircle.svg';
import GridWrapper from '~/src/components/common/GridWrapper';
import { validateFileStructure } from '~/src/scenes/purchaseOrders/orders/validationSchema';
import ImportOrderProducts from '~/src/components/common/orders/ImportOrderProductsForm';

const validationSchema = yup.object().shape({
  xlsx: yup
    .mixed()
    .test('notEmpty', 'Полето е празно', (value) => {
      if (!value || value.length === 0) {
        return false;
      }
      return true;
    })
    .test('fileSize', 'Файлът трябва да бъде под 3MB', (value) => {
      if (value && value.length > 0) {
        return value[0].size <= UPLOAD_MAX_SIZE_IMAGE;
      }
      return true;
    })
    .test(
      'fileFormat',
      'Файлът не е в правилния формат',
      async function (value) {
        if (value && value.length > 0) {
          const validationResult = await validateFileStructure(value[0]);
          if (!validationResult.valid) {
            throw this.createError({ message: validationResult.error });
          }
          return true;
        }
        return false;
      }
    )
    .required('Полето е празно'),
});

export default function OrderProductSelection(): JSX.Element {
  const { consolidation, id } = useParams<{ consolidation: string; id: string; }>();
  const stateRef = useRef(null);
  const history = useHistory();
  const [orderProducts, setOrderProducts] = useState<OrderProductGetDTO[]>([]);
  const initialValues = { quantity: {} };
  const [tableValues, setTableValues] = useState(initialValues);
  const [isImport, setIsImport] = useState<boolean>(false);
  const [result, setResult] = React.useState(null);
  const [gettingProducts, setGettingProducts] = useState<boolean>(false);
  const [disabled, setDisabled] = useState(false);

  const url = `${API_URL}/products/products/get-order-list/order/${id}`;
  const StatefullGrid = useMemo(() => withState(Grid, url, { pageable: { pageSizes: [50, 100, 150] } }), [url]);

  const getProducts = async () => {
    setDisabled(true);
    setGettingProducts(true);
    const result = await OrderProductsApi.getForOrder(id);
    setOrderProducts(result.data);
    const vals = { ...tableValues };
    result.data?.forEach((product) => {
      vals.quantity = { ...vals.quantity, [product.id]: product.originalQuantityDu };
    });
    setTableValues(vals);
    setGettingProducts(false);
    setDisabled(false);
  };

  useEffect(() => {
    if (id) {
      getProducts();
    }
  }, []);

  const handleAddProduct = async (item, ref) => {
    const data = { productId: item.id, orderId: id, originalQuantityDu: ref?.current?.valueAsNumber };
    if (isNaN(data.originalQuantityDu)) {
      return false;
    }

    const result = await OrderProductsApi.create(data);
    if (result.status === StatusCodes.OK) {
      getProducts();
    }
  };

  const handleProductUpdate = (product, event) => {
    var newValue = event.target.valueAsNumber;
    var newOrderProducts = [];
    for (let index = 0; index < orderProducts.length; index++) {
      const op = orderProducts[index];
      if (op.id === product.id) {
        op.originalQuantityDu = newValue;
      }
      newOrderProducts.push(op);
    }
    setOrderProducts(newOrderProducts);
  };

  const handleProductSave = async (product, e) => {
    const result = await OrderProductsApi.update(product.id, { originalQuantityDu: e.target.valueAsNumber });
    if (result.status === StatusCodes.OK) {
      const newOrderProducts = orderProducts.map((op) => {
        if (op.id === product.id) {
          op = result.data;
        }
        return op;
      });
      setOrderProducts(newOrderProducts);
    }
  };

  const handleDeleteProduct = async (product) => {
    await OrderProductsApi.delete(product.id);
    getProducts();
  };

  const productNameCell = ({ dataItem }) => {
    const ref = React.createRef();
    return (
      <td>
        <Form>
          <Form.Row>
            <StyleCol xs={5}>
              <ActionsWrapper style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <StyleInput type="number" className="form-control " ref={ref} />
                <WrapperPlusIcon>
                  <BorderlessButton
                    disabled={disabled}
                    isSubmitting={disabled}
                    onClick={() => handleAddProduct(dataItem, ref)}
                    iconSize={25}
                    icon={plus}
                  />
                </WrapperPlusIcon>
              </ActionsWrapper>
            </StyleCol>
            <StyleCol xs={7}>
              <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                {dataItem.duPerPallet && <PalletDu>{dataItem.duPerPallet}</PalletDu>}
                <ProductName>{dataItem.name}</ProductName>
              </div>
            </StyleCol>
          </Form.Row>
        </Form>
      </td>
    );
  };

  const barcodeCell = ({ dataItem }) => {
    return (
      <td>
        <div>
          <GtinComponent text="CU" code={dataItem.gtinCu} background="#F4F4F4" />
          <GtinComponent text="DU" code={dataItem.gtinDu} background="#F4F4F4" />
        </div>
      </td>
    );
  };

  const onSubmitMine = async (values, setSubmitting) => {
    setSubmitting(true);
    const files = values.xlsx;
    if (files && files.length > 0) {
      const fileData = { files, type: FILE_UPLOAD_TYPES.PRODUCT_SELECTION };
      const fileUploadResp = await FilesApi.create(fileData);
      if (fileUploadResp.status === StatusCodes.OK) {
        const result = await OrderProductsApi.importOrderProducts(fileUploadResp.data[0].id, id);
        setResult(result.data);
        if (result.status === StatusCodes.OK) {
          setIsImport(!isImport);
        }
      }
    }

    await getProducts();
    setSubmitting(false);
  };

  return (
    <WhitePad isFluid>
      <SecondaryNavigation isConsolidation={consolidation} />

      <TitleComponent
        titleRight="Add"
        iconRight={importIcon}
        onLeftHandler={(): void => {
          history.push('/orders-list');
        }}
        onRightHandler={() => {
          setIsImport(!isImport);
        }}
        title="Order Products"
      />
      {isImport && (
        <Wrapper>
          <ModalWrapper isOpen={isImport}>
            <AlignModal>
              <TitleModalWrapper>
                <TitleComponent
                  Heading="h3"
                  title="Import Instructions"
                />
              </TitleModalWrapper>
              <WrapperCloseBtn>
                <BorderlessButton
                  icon={xCircle}
                  iconSize={35}
                  onClick={() => {
                    setIsImport(!isImport);
                  }}
                />
              </WrapperCloseBtn>
              <BoxWrapper>
                <div style={{ marginBottom: 47 }}>
                  <ImportOrderProducts />
                </div>
                <Formik
                  initialValues={{
                    xlsx: [],
                  }}
                  validationSchema={validationSchema}
                  onSubmit={(values, { setSubmitting }) => onSubmitMine(values, setSubmitting)}
                >
                  {({
                    values,
                    errors,
                    handleChange,
                    handleBlur,
                    isSubmitting,
                    setFieldValue,
                    onSubmit,
                    handleSubmit,
                  }) => (
                    <form autoComplete="off" onSubmit={handleSubmit} style={{ width: 480 }}>
                      {console.log('errors', errors)}
                      {console.log('errors.xlsx', errors.xlsx)}
                      <FormRow style={{ width: 480 }}>
                        <FileInputComponent
                          name="xlsx"
                          value={values.xlsx}
                          onBlur={handleBlur}
                          setFieldValue={setFieldValue}
                          cols={24}
                          errorMessage={errors.xlsx}
                        />
                      </FormRow>
                      <SecondaryButton icon={upload} type="submit" isSubmitting={isSubmitting} title="Upload" />
                    </form>
                  )}
                </Formik>
                <ImportResults result={result} />
              </BoxWrapper>
            </AlignModal>
          </ModalWrapper>
        </Wrapper>
      )}

      {!isImport && (
        <>
          <WrapperTables className="row">
            <div className="col-6 table">
              <GridWrapper>
                <TableWrapper>
                  <StatefullGrid ref={stateRef}>
                    <GridColumn
                      field="name"
                      title="Product Name"
                      cell={(props): JSX.Element => productNameCell(props, handleAddProduct, 'Add')}
                    />
                    <GridColumn
                      field="gtin"
                      title="Barcode"
                      cell={barcodeCell}
                      sortable={false}
                    />
                    <GridColumn
                      field="availableQuantities"
                      title="Available Quantities"
                      filterable={false}
                      sortable={false}
                      cell={(props): JSX.Element => (
                        <td>
                          <DisplayQuantities
                            cuQuantity={props.dataItem.availableCu}
                            dpQuantity={props.dataItem.availableDp}
                            duQuantity={props.dataItem.availableDu}
                          />
                        </td>
                      )}
                    />
                  </StatefullGrid>
                </TableWrapper>
              </GridWrapper>
            </div>

            <div className="col-6 table-responsive">
              {gettingProducts ? (
                <PuffLoader loading={true} size={25} />
              ) : (
                <div className="table-responsive">
                  <div className="table table-condensed">
                    <GridWrapper>
                      <TableWrapper>
                        <thead>
                          <tr>
                            <th style={{ width: '96px' }}>Initial Dus</th>
                            <th>Actual Quantities</th>
                            <th>Total Cus</th>
                            <th>Name</th>
                            <th>Barcodes</th>
                            <th style={{ width: '30px !important' }}>Actions</th>
                          </tr>
                        </thead>
                        {orderProducts.length != 0 ? (
                          <tbody>
                            {orderProducts.map((product) => {
                              const totalCus = ProductService.getTotalCu(
                                product.product.packagedProducts,
                                product.quantityDu ?? 0,
                                0,
                                0,
                              );
                              return (
                                <tr key={product.id}>
                                  <StyledTd style={{ width: '96px !important' }}>
                                    {product?.originalQuantityDu != null && (
                                      <TextInputComponent
                                        height={25}
                                        type="number"
                                        className="form-control"
                                        onChange={(e): Promise<void> => handleProductUpdate(product, e)}
                                        onBlur={(e): Promise<void> => handleProductSave(product, e)}
                                        value={product.originalQuantityDu || ''}
                                        cols={24}
                                      />
                                    )}
                                  </StyledTd>
                                  <td>
                                    <DisplayQuantities
                                      cuQuantity={product.quantityCu}
                                      dpQuantity={product.quantityDp}
                                      duQuantity={product.quantityDu}
                                    />
                                  </td>
                                  <td>
                                    <ProductName>{totalCus || 0}</ProductName>
                                  </td>
                                  <td>
                                    <ProductName>{product.productName}</ProductName>
                                  </td>
                                  <td>
                                    <GtinComponent text="CU" code={product.gtinCu} background="#F4F4F4" />

                                    <GtinComponent text="DU" code={product.gtinDu} background="#F4F4F4" />
                                  </td>
                                  <td style={{ width: '20px', margin: 0 }}>
                                    {product?.originalQuantityDu != null && (
                                      <WrapperActionBtnRemove>
                                        <BorderlessButton
                                          onClick={(): Promise<void> => handleDeleteProduct(product)}
                                          icon={xIcon}
                                          iconSize={25}
                                        />
                                      </WrapperActionBtnRemove>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                            <tr>
                              <td>
                                <b>{orderProducts.reduce((s, a) => s + (a.quantityDu ?? 0), 0)}</b>
                              </td>
                              <td>
                                <b>
                                  {
                                    `${orderProducts.reduce((s, a) => s + (a.quantityDu ?? 0), 0)}/
                                     ${orderProducts.reduce((s, a) => s + (a.quantityCu ?? 0), 0)}/
                                     ${orderProducts.reduce((s, a) => s + (a.quantityDp ?? 0), 0)}`
                                  }
                                </b>
                              </td>
                              <td>
                                <b>
                                  {orderProducts.reduce((s, product) =>
                                    s + ProductService.getTotalCu(
                                      product.product.packagedProducts,
                                      product.quantityDu ?? 0,
                                      0,
                                      0,
                                    ), 0)}
                                </b>
                              </td>
                              <td></td>
                              <td></td>
                              <td></td>
                            </tr>
                          </tbody>
                        ) : (
                          <StyleEmptyDiv>No record avaliable</StyleEmptyDiv>
                        )}
                      </TableWrapper>
                    </GridWrapper>
                  </div>
                </div>
              )}
            </div>
          </WrapperTables>
        </>
      )}
    </WhitePad>
  );
}

const WrapperCloseBtn = styled.div`
  position: absolute;
  top: 30px;
  right: 30px;
  z-index: 9999;
`;

const StyleEmptyDiv = styled.tr`
  position: absolute;
  height: 33px;
  z-index: 999;
  left: 0;
  right: 0;
  border: 1px solid #eee;
  border-radius: 5px;
  padding: 7px;
  margin: 0 15px;
  white-space: nowrap;
  color: #6e6d6e;
`;

const StyleInput = styled.input`
  height: 25px !important;
  padding: 0 !important;
  padding-left: 10px !important;
  border-color: #eeeeee !important;
  border: 1px solid #eeeeee;
  border-radius: 200px;

  &:active {
    box-shadow: none !important;
    border: 1px solid #eeeeee;
    border-color: #eeeeee !important;
  }
  &:hover {
    box-shadow: none !important;
    border: 1px solid #eeeeee;
    border-color: #eeeeee !important;
  }
  &:focus {
    box-shadow: none !important;
    border: 1px solid #eeeeee;
    border-color: #eeeeee !important;
  }
`;

const Wrapper = styled.div``;

const WrapperTables = styled.div``;

const AlignModal = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const StyledTd = styled.td`
  .form-group {
    margin: 0;
  }
`;

const StyleCol = styled(Col)`
  padding: 0 !important;
  display: flex;
`;

const WrapperActionBtnRemove = styled.div`
  margin: 0;
`;

const ActionsWrapper = styled.div`
  .form-group {
    margin: 0;
    padding: 0;
    border: 0;
  }
`;

const PalletDu = styled.p`
  color: #343434;
  border-radius: 3px;
  border: 2px solid #343434;
  font-size: 14px;
  line-height: 14px;
  font-weight: 500;
  padding: 2px;
  margin-right: 10px;
  margin-bottom: 0;
  text-align: left;
`;

const ProductName = styled.p`
  color: #343434;
  font-size: 14px;
  font-weight: 500;
  line-height: 17px;
  margin-bottom: 0;
  text-align: left;
`;

const WrapperPlusIcon = styled.div`
  margin: 0 8px 0 3px;
`;

const TitleModalWrapper = styled.div`
  width: 912px;
`;

const BoxWrapper = styled.div`
  background-color: #f4f4f4;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  flex-direction: column;
  padding: 57px 156px;
  border-radius: 10px;
`;
